import { useState } from 'react';

import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import type { ShopStatus, ShopSummary } from '../../api/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { ConfirmDialog } from '../../components/ConfirmDialog.tsx';
import { STATUS_CAPTIONS, STATUS_ORDER, statusLabel } from './shopsHelpers.ts';

const STATUS_TONES: Record<ShopStatus, 'success' | 'warning' | 'error'> = {
  active: 'success',
  suspended: 'warning',
  disabled: 'error',
};

export interface ShopStatusControlProps {
  shop: ShopSummary;
  canWrite: boolean;
  onChanged: () => void;
}

export function ShopStatusControl({ shop, canWrite, onChanged }: Readonly<ShopStatusControlProps>): React.JSX.Element {
  const { api } = useAuth();
  const [pending, setPending] = useState<ShopStatus | null>(null);

  return (
    <Box sx={{ minWidth: { xs: '100%', md: 330 }, flex: '1 1 330px' }}>
      <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mb: 0.5 }}>
        Shop status
      </Typography>
      <ToggleButtonGroup
        exclusive
        value={shop.status}
        disabled={!canWrite}
        onChange={(_, value: ShopStatus | null) => {
          if (value !== null && value !== shop.status) setPending(value);
        }}
        aria-label="shop-status"
        size="small"
      >
        {STATUS_ORDER.map((s) => (
          <ToggleButton
            key={s}
            value={s}
            aria-label={`status-${s}`}
            color={STATUS_TONES[s]}
            sx={{
              color: `${STATUS_TONES[s]}.main`,
              '&.Mui-selected': {
                color: `${STATUS_TONES[s]}.contrastText`,
                bgcolor: `${STATUS_TONES[s]}.main`,
              },
            }}
          >
            {statusLabel(s)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Box sx={{ mt: 0.75, maxWidth: 430 }}>
        <Typography variant="body2" sx={{ opacity: 0.75 }}>
          {STATUS_CAPTIONS[shop.status]}
        </Typography>
      </Box>

      <ConfirmDialog
        open={pending !== null}
        title="Change shop status"
        body={pending ? `Set status to ${statusLabel(pending)}? ${STATUS_CAPTIONS[pending]}` : ''}
        confirmLabel="Change status"
        confirmColor={pending === 'disabled' ? 'error' : 'primary'}
        onConfirm={async () => {
          if (pending) await api.shops.setStatus(shop.id, pending);
        }}
        onClose={() => setPending(null)}
        onSuccess={onChanged}
      />
    </Box>
  );
}

export default ShopStatusControl;
